import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock } from "lucide-react";
import api from "../lib/api";

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    (async () => {
      try { setRecipes(await api.listRecipes()); } catch (e) { console.error(e); }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(recipes.map((r) => r.category).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [recipes]);

  const filtered = useMemo(() => {
    return recipes.filter((r) => {
      if (category !== "All" && r.category !== category) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = [
          r.title, r.subtitle,
          r.related_term, r.related_term_slug, r.related_term_definition,
          r.category,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(s);
      }
      return true;
    });
  }, [recipes, q, category]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="recipes-page">
      <div className="mb-10">
        <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">The Kitchen</div>
        <h1 className="font-display text-5xl md:text-7xl">Recipes</h1>
        <p className="font-body text-lg opacity-80 mt-4 max-w-2xl">
          Every glossary entry earns its keep by pointing at a dish. Here are the dishes.
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="relative max-w-2xl mb-8">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
        <input
          className="input-editorial"
          placeholder="Search recipes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="recipes-search-input"
        />
      </form>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="recipes-category-filter">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`pill ${category === c ? "pill-active" : ""}`}
            data-testid={`recipes-filter-${c.toLowerCase().replace(/\s+/g,'-')}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Gumroad ebook callout removed — Stripe paywall on individual recipes replaces it. */}

      {filtered.length === 0 ? (
        <div className="font-body italic text-lg opacity-70" data-testid="no-recipes">Nothing on the pass. Try another category.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger" data-testid="recipes-grid">
          {filtered.map((r) => (
            <Link key={r.slug} to={`/recipe/${r.slug}`} className="card-editorial flex flex-col" data-testid={`recipe-card-${r.slug}`}>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="font-ui text-xs uppercase tracking-[0.25em] opacity-60 mb-3 flex items-center gap-2">
                  <span>{r.category}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {r.cook_time}</span>
                </div>
                <div className="font-display text-3xl md:text-4xl leading-tight break-words">{r.title}</div>
                <p className="font-body italic text-base md:text-lg opacity-75 mt-3 flex-1 break-words">{r.subtitle}</p>
                <div className="mt-4 font-ui text-sm uppercase tracking-widest hover-underline w-fit">Read recipe</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
